import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  MapPin, Radio, Target, Coins, ArrowUpRight, ShieldCheck,
  Eye, Sparkles, Plus, BadgeCheck, ScanFace, Languages, Star, Smartphone, Tablet,
} from "lucide-react";
import Reveal from "../components/Reveal";
import StoreBadges, { StoreQRCodes } from "../components/StoreBadges";
import DiscoverMockup from "../components/DiscoverMockup";
import { ASSETS, CHAPTERS, STEPS, FAQS } from "../data";

/* Kinetic line reveal for hero headline */
const heroContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineWrap = "block overflow-hidden";
const lineVariant = {
  hidden: { y: "110%", rotate: 3 },
  show: { y: 0, rotate: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

/* App store panel shown in a tilted glowing clipped frame */
const PanelFrame = ({ src, alt, tilt = "6deg", className = "" }) => (
  <div className={`relative group ${className}`} style={{ perspective: "1400px" }}>
    <div
      className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-mc-violet/30 via-mc-pink/20 to-transparent blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-700"
      aria-hidden
    />
    <motion.div
      whileHover={{ rotateY: 0, rotateX: 0, scale: 1.02 }}
      style={{ transform: `rotateY(${tilt}) rotateX(2deg)` }}
      transition={{ type: "spring", stiffness: 120, damping: 18 }}
      className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
    >
      <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/10" />
    </motion.div>
  </div>
);

const ChapterIcon = ({ i }) => {
  const cls = "w-6 h-6 text-mc-pink";
  return [<MapPin className={cls} />, <Radio className={cls} />, <Target className={cls} />, <Coins className={cls} />][i];
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const phoneScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div className="relative bg-mc-bg">
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative min-h-screen spotlight overflow-hidden pt-32 pb-20" data-testid="hero-section">
        <div
          className="absolute inset-0 opacity-[0.06] bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/18182846/pexels-photo-18182846.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: kinetic type */}
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 mb-8">
                <span className="relative flex w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-mc-live opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full w-2 h-2 bg-mc-live" />
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/80">Live now · iOS & Android</span>
              </div>
            </Reveal>

            <motion.h1
              variants={heroContainer}
              initial="hidden"
              animate="show"
              className="font-display font-extrabold text-white tracking-tighter leading-[0.88] text-6xl sm:text-7xl lg:text-8xl"
            >
              <span className={lineWrap}><motion.span variants={lineVariant} className="block">Live where</motion.span></span>
              <span className={lineWrap}><motion.span variants={lineVariant} className="block text-gradient">you are.</motion.span></span>
            </motion.h1>

            <Reveal delay={0.9} className="mt-8 max-w-lg">
              <p className="text-lg md:text-xl text-mc-muted font-light leading-relaxed">
                MapCast is the world's AI-powered, real-time location platform — connecting humanity
                through places. Explore life live from anywhere on Earth, and see the world through
                someone else's eyes.
              </p>
            </Reveal>

            <Reveal delay={1.05}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="#discover"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-mc-bg font-semibold px-7 h-14 hover:scale-105 transition-transform duration-300"
                  data-testid="hero-explore-btn"
                >
                  Explore the map <ArrowUpRight className="w-5 h-5" />
                </a>
                <a
                  href="#download"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white/70 font-semibold px-7 h-14 transition-colors duration-300 hover:border-mc-pink/60 hover:text-white"
                  data-testid="hero-get-app-btn"
                >
                  <span className="w-2 h-2 rounded-full bg-mc-live" /> Get the app
                </a>
              </div>
            </Reveal>

            <Reveal delay={1.2}>
              <div className="mt-12 flex items-center gap-8 font-mono text-xs uppercase tracking-[0.15em] text-mc-muted">
                <div><span className="block text-2xl font-display font-bold text-white tracking-normal">Pay/min</span>watch live</div>
                <div className="w-px h-10 bg-white/10" />
                <div><span className="block text-2xl font-display font-bold text-white tracking-normal">Bounties</span>request a view</div>
                <div className="w-px h-10 bg-white/10" />
                <div><span className="block text-2xl font-display font-bold text-white tracking-normal">Cash out</span>earn castcoins</div>
              </div>
            </Reveal>
          </motion.div>

          {/* Right: cropped device from hero screenshot */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div style={{ y: phoneY, scale: phoneScale }} className="relative float-y">
              <div className="absolute -inset-10 bg-gradient-to-b from-mc-violet/40 via-mc-pink/25 to-transparent blur-3xl rounded-full" aria-hidden />
              <div className="relative w-[260px] sm:w-[300px] h-[560px] sm:h-[640px] rounded-[3rem] overflow-hidden border-[6px] border-zinc-900 shadow-[0_40px_120px_-20px_rgba(124,58,237,0.5)]">
                <div
                  className="absolute inset-0 bg-no-repeat"
                  style={{ backgroundImage: `url('${ASSETS.heroPhone}')`, backgroundSize: "168%", backgroundPosition: "center 86%" }}
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[2.5rem]" />
              </div>
              {/* floating chips */}
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.4 }}
                className="absolute -left-6 top-24 glass rounded-2xl px-4 py-3 flex items-center gap-2 float-y-slow">
                <span className="relative flex w-2.5 h-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-mc-live opacity-75 animate-ping" /><span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-mc-live" /></span>
                <span className="text-sm font-semibold text-white">LIVE · 6 nearby</span>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.55 }}
                className="absolute -right-4 bottom-32 glass rounded-2xl px-4 py-3 flex items-center gap-2 float-y">
                <Coins className="w-4 h-4 text-mc-gold" />
                <span className="text-sm font-semibold text-white">1,240 castcoins</span>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2">
          <div className="w-6 h-10 rounded-full border border-white/20 flex justify-center pt-2">
            <span className="w-1 h-2 rounded-full bg-white/70 scroll-dot" />
          </div>
        </div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <section className="py-10 border-y border-white/10 bg-mc-surface/40" data-testid="marquee-section">
        <Marquee speed={38} gradient={false} className="overflow-hidden">
          {["BROADCAST EVERY MOMENT", "EARN EVERY MINUTE", "THE WORLD IS THE FEED", "ASK FOR A LIVE"].map((t, i) => (
            <span key={i} className="mx-8 flex items-center gap-8">
              <span className={`font-display font-extrabold uppercase text-5xl md:text-7xl ${i % 2 ? "text-stroke-pink" : "text-stroke"}`}>{t}</span>
              <MapPin className="w-8 h-8 text-mc-pink" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* ---------------- MANIFESTO CHAPTERS ---------------- */}
      <section id="discover" className="relative py-28 md:py-40 px-6 lg:px-10" data-testid="chapters-section">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-20 md:mb-32 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-6">How MapCast works</p>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter leading-[0.95] text-white">
              Not a feed. <br /> A living map of <span className="text-gradient">right now.</span>
            </h2>
          </Reveal>

          <div className="space-y-28 md:space-y-40">
            {CHAPTERS.map((c, i) => {
              const media = [null, ASSETS.goLive, ASSETS.dropBounty, ASSETS.mapcaster][i];
              const reverse = i % 2 === 1;
              return (
                <div key={c.num} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                  <Reveal className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                    <div className="flex items-start gap-5">
                      <span className="font-mono text-mc-muted text-lg pt-2">{c.num}</span>
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <ChapterIcon i={i} />
                          <span className="font-mono text-xs uppercase tracking-[0.2em] text-mc-pink">{c.tag}</span>
                        </div>
                        <h3 className="font-display font-bold text-3xl md:text-5xl tracking-tight text-white leading-[1.02]">{c.title}</h3>
                        <p className="mt-6 text-lg text-mc-muted font-light leading-relaxed max-w-md">{c.body}</p>
                      </div>
                    </div>
                  </Reveal>

                  <Reveal delay={0.1} className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                    {i === 0 ? (
                      <DiscoverMockup />
                    ) : (
                      <PanelFrame src={media} alt={c.title} tilt={reverse ? "-5deg" : "5deg"} />
                    )}
                  </Reveal>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- VISION · BEYOND SOCIAL MEDIA ---------------- */}
      <section id="vision" className="relative py-28 md:py-36 overflow-hidden" data-testid="vision-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-6">Beyond social media</p>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter leading-[0.95] text-white">
              Not profiles. <span className="text-gradient">Places.</span>
            </h2>
            <p className="mt-8 text-lg text-mc-muted font-light leading-relaxed">
              Today's social media connects people through profiles, followers, and algorithms.
              MapCast connects them through places, cultures, and real-life experiences — so you
              discover the world through the eyes of the people who actually live there. You don't
              follow influencers. You explore places, meet ordinary people living extraordinary
              everyday lives, and let curiosity turn into understanding.
            </p>
          </Reveal>
        </div>

        <div className="mt-14">
          <Marquee speed={30} gradient={false} className="overflow-hidden">
            {["A fisherman in Türkiye", "A farmer in Argentina", "A street musician in Spain", "A family celebration in India", "A sunrise over Iceland", "A market in Marrakesh", "A festival in Rio"].map((t, i) => (
              <span key={i} className="mx-3 inline-flex items-center gap-3 rounded-full border border-white/12 bg-white/[0.03] px-6 py-3 text-white/85 text-lg font-light">
                <span className="w-1.5 h-1.5 rounded-full bg-mc-pink" /> {t}
              </span>
            ))}
          </Marquee>
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 mt-16 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="glass rounded-3xl p-8 h-full">
              <h3 className="font-display font-bold text-2xl text-white flex items-center gap-3"><Eye className="w-6 h-6 text-mc-pink" /> Our vision</h3>
              <p className="mt-4 text-mc-muted font-light leading-relaxed">
                We believe technology should bring people closer together — not divide them. Our vision
                is to build the world's most trusted AI-powered real-time location platform, where people
                safely explore cultures, communities, and places while building understanding across borders.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass rounded-3xl p-8 h-full">
              <h3 className="font-display font-bold text-2xl text-white flex items-center gap-3"><Sparkles className="w-6 h-6 text-mc-pink" /> Our mission</h3>
              <p className="mt-4 text-mc-muted font-light leading-relaxed">
                To connect humanity through places by using AI to make real-time experiences safer,
                smarter, and accessible to everyone — creating a world where understanding begins with
                seeing life through someone else's eyes.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- HOW IT WORKS (bento) ---------------- */}
      <section id="broadcast" className="relative py-24 px-6 lg:px-10 bg-mc-surface/30 border-y border-white/10" data-testid="steps-section">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-16">
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter text-white">Three taps to the world</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="glass rounded-3xl p-8 h-full hover:border-mc-pink/40 transition-colors duration-500 group">
                  <span className="font-display font-extrabold text-6xl text-white/10 group-hover:text-mc-pink/40 transition-colors duration-500">{s.n}</span>
                  <h3 className="font-display font-bold text-2xl text-white mt-6">{s.title}</h3>
                  <p className="mt-3 text-mc-muted font-light leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- INBOX ---------------- */}
      <section id="inbox" className="relative py-28 px-6 lg:px-10 overflow-hidden" data-testid="inbox-section">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-16 items-center">
          <Reveal className="lg:col-span-6 lg:order-2">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-6">Inbox · Notifications</p>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter leading-[0.95] text-white">
              Never miss a <span className="text-gradient">moment.</span>
            </h2>
            <p className="mt-8 text-lg text-mc-muted font-light leading-relaxed max-w-lg">
              Live alerts, tips, bounties, and follows — all in one inbox. The second someone you
              follow goes live, you'll know, and you're one tap from watching.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {[
                { icon: <Radio className="w-5 h-5" />, t: "Live alerts", d: "Get pinged the instant a MapCaster you follow goes on air." },
                { icon: <Coins className="w-5 h-5" />, t: "Tips & earnings", d: "See every castcoin tip land in your wallet in real time." },
                { icon: <Target className="w-5 h-5" />, t: "Bounty updates", d: "Know when a bounty is accepted, delivered, or refunded." },
                { icon: <Star className="w-5 h-5" />, t: "New followers", d: "Grow your audience and follow back in a tap." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-mc-violet/30 to-mc-pink/30 border border-white/10 flex items-center justify-center text-mc-pink">{f.icon}</span>
                  <div>
                    <h4 className="font-semibold text-white">{f.t}</h4>
                    <p className="text-sm text-mc-muted font-light mt-1">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 lg:order-1">
            <PanelFrame src={ASSETS.inboxTablet} alt="MapCast inbox with live alerts, tips and bounties" tilt="5deg" />
          </Reveal>
        </div>
      </section>

      {/* ---------------- BOUNTIES / SAFETY ---------------- */}
      <section id="bounties" className="relative py-28 px-6 lg:px-10 overflow-hidden" data-testid="safety-section">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-6">Safety-forward · AI-augmented</p>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter leading-[0.95] text-white">
              A serious platform for a <span className="text-gradient">live world.</span>
            </h2>
            <p className="mt-8 text-lg text-mc-muted font-light leading-relaxed max-w-lg">
              MapCast is built to become the world's first AI-responsible live broadcasting
              platform — with privacy and safety controls engineered in, not bolted on.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {[
                { icon: <Eye className="w-5 h-5" />, t: "Location coarsening", d: "Positions blur to ~1 km so you never leak an exact address." },
                { icon: <ShieldCheck className="w-5 h-5" />, t: "Atomic wallets", d: "Every castcoin, tip, and escrow is a single safe transaction." },
                { icon: <Sparkles className="w-5 h-5" />, t: "AI safety (roadmap)", d: "Face & license-plate anonymization, sensitive-location protection, content safety — no 18+, ever." },
                { icon: <Radio className="w-5 h-5" />, t: "Verified MapCasters", d: "Geofencing, crowd & event detection for trusted live." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-mc-violet/30 to-mc-pink/30 border border-white/10 flex items-center justify-center text-mc-pink">{f.icon}</span>
                  <div>
                    <h4 className="font-semibold text-white">{f.t}</h4>
                    <p className="text-sm text-mc-muted font-light mt-1">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <PanelFrame src={ASSETS.dropBounty} alt="Drop a bounty on the map" tilt="-5deg" />
          </Reveal>
        </div>
      </section>

      {/* ---------------- VERIFIED & AI-SAFE ---------------- */}
      <section id="trust" className="relative py-24 px-6 lg:px-10" data-testid="verified-section">
        <div className="mx-auto max-w-6xl">
          <div className="relative rounded-[2.5rem] overflow-hidden glass p-8 md:p-14">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-mc-violet/20 blur-3xl" aria-hidden />
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Badge medallion */}
              <Reveal className="lg:col-span-4 flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-mc-violet to-mc-pink blur-2xl opacity-60 float-y" aria-hidden />
                  <div className="relative w-40 h-40 md:w-52 md:h-52 rounded-full bg-gradient-to-br from-mc-violet to-mc-pink flex items-center justify-center shadow-[0_20px_60px_-10px_rgba(124,58,237,0.6)]">
                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-mc-bg flex items-center justify-center">
                      <BadgeCheck className="w-16 h-16 md:w-20 md:h-20" style={{ stroke: "url(#vg)" }} strokeWidth={1.6} />
                    </div>
                  </div>
                  {/* gradient def for the icon stroke */}
                  <svg width="0" height="0" className="absolute">
                    <defs>
                      <linearGradient id="vg" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#A855F7" />
                        <stop offset="100%" stopColor="#FF3D8B" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </Reveal>

              {/* Copy */}
              <Reveal delay={0.1} className="lg:col-span-8">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-5">Verified & AI-safe</p>
                <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tighter leading-[0.98] text-white">
                  A blue check you can <span className="text-gradient">actually trust.</span>
                </h2>
                <p className="mt-6 text-lg text-mc-muted font-light leading-relaxed max-w-2xl">
                  Verified MapCasters are identity-checked broadcasters marked with a blue badge, so
                  viewers and partners know exactly who's behind the stream. Every live is watched over
                  by AI safety systems built to keep MapCast responsible from day one.
                </p>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  {[
                    { icon: <BadgeCheck className="w-5 h-5" />, t: "Verified MapCasters", d: "A blue check for identity-verified creators you can rely on." },
                    { icon: <ShieldCheck className="w-5 h-5" />, t: "AI content safety", d: "Real-time moderation. 18+ content is never allowed — ever." },
                    { icon: <ScanFace className="w-5 h-5" />, t: "Face-blur & privacy zones", d: "Protect bystanders and sensitive places automatically (roadmap)." },
                    { icon: <Languages className="w-5 h-5" />, t: "Live translate & dubbing", d: "Speech-to-text, translation and voice dubbing across languages (roadmap)." },
                  ].map((f) => (
                    <div key={f.t} className="flex gap-4">
                      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-mc-violet/30 to-mc-pink/30 border border-white/10 flex items-center justify-center text-mc-pink">{f.icon}</span>
                      <div>
                        <h4 className="font-semibold text-white">{f.t}</h4>
                        <p className="text-sm text-mc-muted font-light mt-1">{f.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- DEVICES SHOWCASE ---------------- */}
      <section id="devices" className="relative py-24 px-6 lg:px-10 bg-mc-surface/30 border-y border-white/10" data-testid="devices-section">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-5">Every screen</p>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter text-white leading-[0.98]">
                Built for iPhone &amp; iPad.
              </h2>
            </div>
            <div className="flex items-center gap-5 text-mc-muted">
              <span className="inline-flex items-center gap-2"><Smartphone className="w-5 h-5" /> iPhone</span>
              <span className="inline-flex items-center gap-2"><Tablet className="w-5 h-5" /> iPad</span>
            </div>
          </Reveal>

          <Reveal>
            <PanelFrame src={ASSETS.mapTablet} alt="MapCast live map on iPad" tilt="0deg" className="max-w-5xl mx-auto" />
          </Reveal>

          <div className="grid md:grid-cols-2 gap-8 mt-10 max-w-4xl mx-auto">
            <Reveal delay={0.1}>
              <PanelFrame src={ASSETS.watchLive} alt="Watching a live stream" tilt="4deg" />
            </Reveal>
            <Reveal delay={0.18}>
              <PanelFrame src={ASSETS.inboxPhone} alt="MapCast inbox on iPhone" tilt="-4deg" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- DOWNLOAD CTA ---------------- */}
      <section id="download" className="relative py-24 px-6 lg:px-10" data-testid="download-section">
        <div className="mx-auto max-w-6xl relative rounded-[2.5rem] overflow-hidden border border-white/10 spotlight p-12 md:p-20 text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-mc-violet/20 via-transparent to-mc-pink/20" aria-hidden />
          <Reveal className="relative">
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
              The world is going live.
            </h2>
            <p className="mt-6 text-lg text-mc-muted font-light max-w-xl mx-auto">
              MapCast is live on the App Store and Google Play. Download now and go live.
            </p>
            <div className="mt-10 flex flex-col items-center gap-10">
              <StoreBadges className="justify-center" />
              <StoreQRCodes />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section id="faq" className="relative py-24 px-6 lg:px-10" data-testid="faq-section">
        <div className="mx-auto max-w-4xl">
          <Reveal className="mb-12">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-6">FAQ</p>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter text-white">Questions, answered</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-semibold text-lg md:text-xl text-white hover:no-underline py-6 [&>svg]:hidden group">
                    <span className="flex-1">{f.q}</span>
                    <Plus className="w-5 h-5 text-mc-pink transition-transform duration-300 group-data-[state=open]:rotate-45" />
                  </AccordionTrigger>
                  <AccordionContent className="text-mc-muted font-light text-base leading-relaxed pb-6 pr-8">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
